/**
 * User Profile Drawer Component
 * Allows users to update their profile information including name, avatar, etc.
 */

import React, { useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Avatar,
  Text,
  useToast,
  IconButton,
  Box,
  Divider,
} from '@chakra-ui/react';
import { FaCamera, FaSave, FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

interface UserProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserProfileDrawer: React.FC<UserProfileDrawerProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const toast = useToast();

  // Local state for profile form
  const [displayName, setDisplayName] = useState(user?.email?.split('@')[0] || '');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
      toast({
        title: t('dashboard.logout_success'),
        description: t('dashboard.logout_desc'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('profile.failed_title'),
        description: t('profile.failed_desc'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // TODO: Implement profile update API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      toast({
        title: t('profile.success_title'),
        description: t('profile.success_desc'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onClose();
    } catch (error) {
      toast({
        title: t('profile.failed_title'),
        description: t('profile.failed_desc'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarChange = () => {
    toast({
      title: t('profile.coming_soon'),
      description: t('profile.coming_soon_desc'),
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
    >
      <DrawerOverlay bg="blackAlpha.500" backdropFilter="blur(3px)" />
      <DrawerContent bg="appleElevated">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor="appleCardBorder" pb={4}>
          <Text fontSize="lg" fontWeight="700" color="appleTextPrimary">{t('profile.title')}</Text>
          <Text fontSize="sm" color="appleTextSecondary" fontWeight="normal" mt={1}>
            {t('profile.subtitle')}
          </Text>
        </DrawerHeader>

        <DrawerBody>
          <VStack spacing={6} align="stretch" pt={4}>
            {/* Avatar Section */}
            <VStack spacing={3}>
              <Box position="relative">
                <Avatar
                  size="2xl"
                  name={displayName || user?.email || 'User'}
                  src={avatarUrl}
                  bg="appleAccent"
                />
                <IconButton
                  aria-label={t('profile.avatar_aria')}
                  icon={<FaCamera />}
                  size="sm"
                  colorScheme="blue"
                  borderRadius="full"
                  position="absolute"
                  bottom={0}
                  right={0}
                  onClick={handleAvatarChange}
                />
              </Box>
              <Text fontSize="sm" color="appleTextSecondary">
                {t('profile.avatar_helper')}
              </Text>
            </VStack>

            <Divider borderColor="appleCardBorder" />

            {/* Profile Form */}
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('profile.email')}
                </FormLabel>
                <Input
                  variant="flushed"
                  borderColor="appleCardBorder"
                  value={user?.email || ''}
                  isReadOnly
                  cursor="not-allowed"
                />
                <Text fontSize="xs" color="appleTextSecondary" mt={1}>
                  {t('profile.email_helper')}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('profile.display_name')}
                </FormLabel>
                <Input
                  variant="flushed"
                  borderColor="appleCardBorder"
                  focusBorderColor="appleAccent"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t('profile.display_name_placeholder')}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('profile.full_name')}
                </FormLabel>
                <Input
                  variant="flushed"
                  borderColor="appleCardBorder"
                  focusBorderColor="appleAccent"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t('profile.full_name_placeholder')}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('profile.phone')}
                </FormLabel>
                <Input
                  variant="flushed"
                  borderColor="appleCardBorder"
                  focusBorderColor="appleAccent"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('profile.phone_placeholder')}
                  type="tel"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('profile.broker')}
                </FormLabel>
                <Input
                  variant="flushed"
                  borderColor="appleCardBorder"
                  value={typeof user?.broker === 'string' ? user.broker : user?.broker?.name || 'Not assigned'}
                  isReadOnly
                  cursor="not-allowed"
                />
                <Text fontSize="xs" color="appleTextSecondary" mt={1}>
                  {t('profile.broker_helper')}
                </Text>
              </FormControl>

              <Divider borderColor="appleCardBorder" />

              {/* Language Selector */}
              <FormControl>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('profile.language_label')}
                </FormLabel>
                <HStack spacing={4}>
                  <Button
                    size="sm"
                    colorScheme={language === 'BR-pt' ? 'blue' : 'gray'}
                    variant={language === 'BR-pt' ? 'solid' : 'outline'}
                    onClick={() => setLanguage('BR-pt')}
                    width="full"
                  >
                    {t('profile.lang_pt')}
                  </Button>
                  <Button
                    size="sm"
                    colorScheme={language === 'US-en' ? 'blue' : 'gray'}
                    variant={language === 'US-en' ? 'solid' : 'outline'}
                    onClick={() => setLanguage('US-en')}
                    width="full"
                  >
                    {t('profile.lang_en')}
                  </Button>
                </HStack>
              </FormControl>
            </VStack>
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="appleCardBorder">
          <HStack spacing={3} width="full" justify="space-between">
            <Button
              variant="outline"
              colorScheme="red"
              size="sm"
              onClick={handleLogout}
            >
              {t('profile.logout_btn')}
            </Button>
            <HStack spacing={3}>
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                leftIcon={<FaTimes />}
              >
                {t('profile.cancel_btn')}
              </Button>
              <Button
                colorScheme="blue"
                size="sm"
                onClick={handleSave}
                isLoading={isSaving}
                leftIcon={<FaSave />}
              >
                {t('profile.save_btn')}
              </Button>
            </HStack>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default UserProfileDrawer;
